import { createElement, memo, useCallback, useState } from 'react';
import * as LucideIcons from 'lucide-react';
const { CheckSquare, Square } = LucideIcons;
import Badge from './Badge.jsx';
import Button from './Button.jsx';
import Dropdown from './Dropdown.jsx';
import IconHighlight from './IconHighlight.jsx';
import ProgressBar from './ProgressBar.jsx';
import Tooltip from './Tooltip.jsx';

const CARD_BODY_SURFACE_CLASS = 'bg-surface dark:bg-gray-800/80';
const FOOTER_BAR_SURFACE_CLASS = 'bg-surface-muted/30 dark:bg-gray-900/40';
const FOOTER_RIGHT_SLOT_CLASS = 'ml-auto inline-flex min-w-0 shrink-0 items-center gap-1.5';
const FOOTER_RIGHT_SLOT_STACKED_CLASS = 'ml-auto inline-flex min-w-0 shrink-0 items-start gap-1.5';

function getMetaItemKey(item, idx, prefix) {
  if (item?.id != null) return `${prefix}-${item.id}`;
  if (typeof item?.key === 'string' && item.key.trim() !== '') return `${prefix}-${item.key.trim()}`;
  const label = item?.text ?? item?.label ?? '';
  const value = item?.value ?? '';
  if (label !== '' || value !== '') return `${prefix}-${String(label)}-${String(value)}`;
  return `${prefix}-${idx}`;
}

function joinClassNames(...values) {
  return values.filter(Boolean).join(' ');
}

const EntityListCardSurface = memo(function EntityListCardSurface({ stacked = false, children }) {
  if (stacked) {
    return (
      <div className={joinClassNames(CARD_BODY_SURFACE_CLASS, 'min-h-full w-full')}>
        <div className={joinClassNames(FOOTER_BAR_SURFACE_CLASS, 'min-h-full w-full')}>{children}</div>
      </div>
    );
  }

  return <div className={joinClassNames(CARD_BODY_SURFACE_CLASS, 'min-h-full w-full')}>{children}</div>;
});

// ─── Tooltip helper ───────────────────────────────────────────────────────────

function resolveOptionalTooltip(value) {
  if (value == null || value === false) return null;
  if (typeof value === 'string') return value.trim() === '' ? null : value;
  return value;
}

// ─── Icon resolver ────────────────────────────────────────────────────────────

const DEFAULT_ICON = LucideIcons.Folder;
const DEFAULT_MENU_ICON = LucideIcons.MoreVertical;

function resolveLucideIcon(iconName) {
  if (!iconName || typeof iconName !== 'string') return null;
  if (LucideIcons[iconName]) return LucideIcons[iconName];
  const pascal = iconName
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1).toLowerCase())
    .join('');
  if (pascal && LucideIcons[pascal]) return LucideIcons[pascal];
  return null;
}

function renderLucideIcon(icon, iconName, props = {}) {
  const Icon = icon || resolveLucideIcon(iconName);
  return Icon ? createElement(Icon, { 'aria-hidden': true, ...props }) : null;
}

// ─── Title badge ──────────────────────────────────────────────────────────────

const TITLE_BADGE_STYLES = {
  promotable: 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300',
  promoted: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  conflict: 'border-red-500/40 bg-red-500/10 text-red-300',
  not_promotable: 'border-edge bg-surface-muted text-content-muted',
};

const TitleBadge = memo(function TitleBadge({ label, variant, icon: Icon, className }) {
  const variantClass = TITLE_BADGE_STYLES[variant] ?? '';
  return (
    <span
      className={[
        'inline-flex items-center gap-0.5 rounded-md border px-2 py-0.5 text-[10px] font-medium',
        variantClass,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {Icon && <Icon className="size-2.5 shrink-0" aria-hidden />}
      {label}
    </span>
  );
});

// ─── Header action item (badge | button) ─────────────────────────────────────

/**
 * Dispatches each item in `headerBadges` to `<Badge>` or `<Button>` based on `type`.
 *
 * badge (default):
 *   { type?: 'badge', label, tooltip?, tooltipPosition?, tooltipAlign?, tooltipSize?, tooltipClassName?, onClick?, variant?, size?, ...badgeProps }
 *
 * button:
 *   { type: 'button', label, icon?, iconName?, variant?, size?, onClick?, tooltip?, tooltipPosition?, tooltipAlign?, tooltipSize?, tooltipClassName?, className? }
 */
const HeaderAction = memo(function HeaderAction({ item, idx }) {
  const {
    type = 'badge',
    label,
    tooltip,
    tooltipPosition = 'top',
    tooltipAlign = 'end',
    tooltipSize = 'compact',
    tooltipClassName,
    onClick,
    icon,
    iconName,
    variant,
    size,
    className,
    ...rest
  } = item;

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      onClick?.(e);
    },
    [onClick],
  );

  if (!label) return null;

  let node;

  if (type === 'button') {
    const Icon = icon || resolveLucideIcon(iconName);
    node = (
      <Button
        key={`header-btn-${idx}`}
        type="button"
        variant={variant ?? 'primary'}
        size={size ?? 'sm'}
        leftIcon={Icon}
        className={[
          '!h-7 !gap-1.5 !rounded-lg !px-2.5 !text-xs',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={onClick ? handleClick : undefined}
      >
        {label}
      </Button>
    );
  } else {
    node = (
      <Badge
        key={`header-badge-${idx}`}
        {...rest}
        variant={variant}
        onClick={onClick ? handleClick : undefined}
      >
        {label}
      </Badge>
    );
  }

  const tip = resolveOptionalTooltip(tooltip);
  if (tip == null) return node;

  return (
    <Tooltip
      key={`header-action-tooltip-${idx}`}
      content={tip}
      position={tooltipPosition === 'bottom' ? 'bottom' : 'top'}
      align={tooltipAlign === 'start' || tooltipAlign === 'center' || tooltipAlign === 'end' ? tooltipAlign : 'end'}
      size={tooltipSize === 'default' ? 'default' : 'compact'}
      className={typeof tooltipClassName === 'string' ? tooltipClassName.trim() : undefined}
    >
      {node}
    </Tooltip>
  );
});

// ─── Footer meta item ─────────────────────────────────────────────────────────

const FooterMetaItem = memo(function FooterMetaItem({ item }) {
  const label = item.text ?? item.label ?? '';
  const hasClick = typeof item.onClick === 'function';
  const colorClass = typeof item.color === 'string' ? item.color.trim() : '';
  const textClassName = typeof item.textClassName === 'string' ? item.textClassName.trim() : '';
  const iconClassName = typeof item.iconClassName === 'string' ? item.iconClassName.trim() : '';
  const containerClassName = typeof item.className === 'string' ? item.className.trim() : '';
  const isBadge = item?.type === 'badge';
  const badgeBorderColor = typeof item.badgeBorderColor === 'string' ? item.badgeBorderColor.trim() : '';
  const badgeBackground = typeof item.badgeBackground === 'string' ? item.badgeBackground.trim() : '';

  const variantClass = isBadge
    ? ['rounded-full border px-2 py-0.5', badgeBorderColor || 'border-edge', badgeBackground || 'bg-transparent']
        .filter(Boolean)
        .join(' ')
    : '';

  const iconSize = isBadge ? 11 : 12;
  const iconElement = renderLucideIcon(item.icon, item.iconName, {
    size: iconSize,
    className: [colorClass, iconClassName].filter(Boolean).join(' '),
  });

  const baseClassName = [
    'inline-flex items-center gap-1.5 text-xs',
    colorClass || 'text-content-muted',
    variantClass,
    hasClick ? 'cursor-pointer transition-opacity hover:opacity-90' : '',
    containerClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {iconElement}
      {label !== '' ? (
        <span className={['leading-none', textClassName].filter(Boolean).join(' ')}>{label}</span>
      ) : null}
      {item.value != null ? (
        <span className={['leading-none font-medium', colorClass, textClassName].filter(Boolean).join(' ')}>
          {item.value}
        </span>
      ) : null}
    </>
  );

  if (hasClick) {
    return (
      <button
        type="button"
        className={[
          baseClassName,
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={(e) => {
          e.stopPropagation();
          item.onClick(e);
        }}
        onKeyDown={(e) => e.stopPropagation()}
        aria-label={item.ariaLabel || item.title || label}
        title={item.title}
      >
        {content}
      </button>
    );
  }

  return (
    <span className={baseClassName} title={item.title}>
      {content}
    </span>
  );
});

// ─── Footer right item ────────────────────────────────────────────────────────

const FooterRightItem = memo(function FooterRightItem({ item }) {
  const baseColor =
    typeof item.color === 'string' && item.color.trim() ? item.color.trim() : 'text-content-secondary';
  const className = typeof item.className === 'string' ? item.className.trim() : '';
  const label = item.label ?? item.text ?? '';
  const hasClick = typeof item.onClick === 'function';
  const iconElement = renderLucideIcon(item.icon, item.iconName, {
    size: 12,
    className: baseColor,
  });
  const commonClassName = [
    'inline-flex items-center gap-1.5 text-xs leading-none',
    baseColor,
    hasClick ? 'cursor-pointer transition-opacity hover:opacity-90' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (hasClick) {
    return (
      <button
        type="button"
        className={[
          commonClassName,
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={(e) => {
          e.stopPropagation();
          item.onClick(e);
        }}
        onKeyDown={(e) => e.stopPropagation()}
        aria-label={item.ariaLabel || item.title || label}
        title={item.title}
      >
        {iconElement}
        <span>{label}</span>
      </button>
    );
  }

  return (
    <span className={commonClassName} title={item.title}>
      {iconElement}
      <span>{label}</span>
    </span>
  );
});

const FooterRightSlot = memo(function FooterRightSlot({ footerRight, footerRightItems, stacked = false }) {
  const slotClassName = stacked ? FOOTER_RIGHT_SLOT_STACKED_CLASS : FOOTER_RIGHT_SLOT_CLASS;

  if (footerRight) {
    return <div className={slotClassName}>{footerRight}</div>;
  }

  if (!Array.isArray(footerRightItems) || footerRightItems.length === 0) {
    return null;
  }

  return (
    <div className={slotClassName}>
      {footerRightItems.map((item, idx) => (
        <FooterRightItem key={getMetaItemKey(item, idx, 'fr')} item={item} />
      ))}
    </div>
  );
});

const EntityListCardBodyText = memo(function EntityListCardBodyText({
  bodyText,
  bodyTextMaxLength,
  bodyIcon: BodyIcon,
  moreLabel,
  lessLabel,
}) {
  const [showFullBody, setShowFullBody] = useState(false);
  const toggleFullBody = useCallback((e) => {
    e.stopPropagation();
    setShowFullBody((previous) => !previous);
  }, []);
  const shouldTruncate = bodyText.length > bodyTextMaxLength;
  const displayText =
    shouldTruncate && !showFullBody ? `${bodyText.slice(0, bodyTextMaxLength).trim()}…` : bodyText;

  return (
    <div className="mt-1 flex min-w-0 items-start gap-1.5">
      {BodyIcon ? (
        <BodyIcon className="mt-0.5 size-3.5 shrink-0 text-content-muted" aria-hidden />
      ) : null}
      <div className="min-w-0 flex-1">
        <p className={joinClassNames('text-xs text-content-muted', showFullBody ? '' : 'line-clamp-1')}>
          {displayText}
        </p>
        {shouldTruncate ? (
          <button
            type="button"
            className="mt-0.5 text-xs text-sky-400/90 transition-colors hover:text-sky-300"
            onClick={toggleFullBody}
          >
            {showFullBody ? lessLabel : moreLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
});

// ─── EntityListCard ───────────────────────────────────────────────────────────

/**
 * Primitive card for entity list rows. Standardizes the layout zones, visual
 * states and declarative data-driven API for all entity list views.
 *
 * **Header**
 * @param {string}  props.title
 *   Primary title of the card.
 *
 * @param {string}  [props.code]
 *   Monospace prefix badge rendered before the title (e.g. "BU-001").
 *
 * @param {{ label: string, variant?: 'promotable'|'promoted'|'conflict'|'not_promotable', icon?: LucideIcon, className?: string }} [props.titleBadge]
 *   Semantic badge rendered inline after the title.
 *
 * @param {object}  [props.iconHighlightProps]
 *   Props forwarded to `<IconHighlight>` (icon, size, bgColor, iconColor…).
 *
 * @param {string}  [props.iconLabel]
 *   Short label rendered below the icon (e.g. "PDF", "CSV"). Uppercase, truncated.
 *
 * @param {Array<{
 *   type?: 'badge'|'button',
 *   label: string,
 *   icon?: LucideIcon,
 *   iconName?: string,
 *   variant?: string,
 *   size?: 'sm'|'md'|'lg',
 *   onClick?: function,
 *   tooltip?: string|import('react').ReactNode,
 *   tooltipPosition?: 'top'|'bottom',
 *   tooltipAlign?: 'start'|'center'|'end',
 *   tooltipSize?: 'compact'|'default',
 *   tooltipClassName?: string,
 *   className?: string,
 *   [badgeProps]: any
 * }>} [props.headerBadges=[]]
 *   Header action items. Each item dispatches to `<Badge>` (type 'badge', default)
 *   or `<Button>` (type 'button'). Both support `onClick` and an optional tooltip
 *   with full position/align/size control.
 *
 * @param {Array<{ icon?: LucideIcon, iconName?: string, ariaLabel?: string, title?: string, className?: string, onClick?: function }>} [props.headerIcons=[]]
 *   Icon-only action buttons rendered next to the badge/button area.
 *
 * @param {boolean} [props.showHeaderIconsOnHover=false]
 *   When true, `headerIcons` are hidden until the card is hovered.
 *
 * @param {{ props?: object, items: Array<{ label: string, icon?: LucideIcon, iconName?: string, variant?: string, separator?: boolean, onClick?: function }> }} [props.dropdown]
 *   Kebab-menu descriptor. `dropdown.items` is required; `dropdown.props` is spread onto `<Dropdown>`.
 *
 * **Body**
 * @param {import('react').ReactNode} [props.children]
 *   Free-form JSX content below the title. Use when you need rich markup.
 *
 * @param {string}  [props.bodyText]
 *   Plain-text body with built-in expand/collapse. Rendered below the title
 *   (after `children` if both are provided). Takes `bodyTextMaxLength` characters
 *   before showing a "more" button.
 *
 * @param {number}  [props.bodyTextMaxLength=120]
 *   Character limit before the expand toggle appears.
 *
 * @param {import('lucide-react').LucideIcon} [props.bodyIcon]
 *   Optional icon shown to the left of `bodyText`.
 *
 * @param {string}  [props.moreLabel='more']
 *   i18n override for the expand label.
 *
 * @param {string}  [props.lessLabel='less']
 *   i18n override for the collapse label.
 *
 * @param {{ value: number|string, label?: import('react').ReactNode, tone?: 'green'|'amber'|'red'|'blue'|'purple'|'neutral', showLabel?: boolean, ariaLabel?: string, className?: string, trackClassName?: string, barClassName?: string, labelClassName?: string }} [props.progress]
 *   Optional percentage bar rendered below the body content. `value` is clamped
 *   to the 0-100 range.
 *
 * **Footer left**
 * @param {'default'|'stacked'} [props.footerLayout='default']  Footer arrangement. `stacked` uses two meta rows on the left and the default footer surface across the full card.
 * @param {Array<{ id?: string|number, key?: string, icon?: LucideIcon, iconName?: string, text?: string, label?: string, value?: string|number, color?: string, type?: 'default'|'badge', badgeBorderColor?: string, badgeBackground?: string, onClick?: function, className?: string }>} [props.footerMetaItems=[]]
 * @param {Array<{ id?: string|number, key?: string, icon?: LucideIcon, iconName?: string, text?: string, label?: string, value?: string|number, color?: string, type?: 'default'|'badge', badgeBorderColor?: string, badgeBackground?: string, onClick?: function, className?: string }>} [props.footerMetaSecondaryItems=[]]
 *   Second footer row when `footerLayout="stacked"`.
 *
 * **Footer right**
 * @param {Array<{ icon?: LucideIcon, iconName?: string, label?: string, text?: string, color?: string, onClick?: function, className?: string }>} [props.footerRightItems=[]]
 * @param {import('react').ReactNode} [props.footerRight=null]
 *   Arbitrary node for the footer-right slot (takes precedence over `footerRightItems`).
 *
 * **States**
 * @param {boolean} [props.selected=false]  Cyan/violet ring (item open in drawer).
 * @param {boolean} [props.hasError=false]  Red border (failed / conflict state).
 * @param {function} [props.onClick]        Makes the whole card clickable.
 * @param {string}  [props.className]       Extra classes on the root shell (layout, spacing, borders). Surface colors are applied on an inner layer.
 */
const EntityListCard = ({
  // Header
  title,
  code,
  titleBadge,
  iconHighlightProps,
  iconLabel,
  selectable = false,
  onSelect,
  headerBadges = [],
  headerIcons = [],
  showHeaderIconsOnHover = false,
  dropdown,

  // Body
  children,
  bodyText,
  bodyTextMaxLength = 120,
  bodyIcon: BodyIcon,
  moreLabel = 'more',
  lessLabel = 'less',
  progress,

  // Footer
  footerLayout = 'default',
  footerMetaItems = [],
  footerMetaSecondaryItems = [],
  footerRightItems = [],
  footerRight = null,

  // States
  selected = false,
  hasError = false,
  onClick,
  className = '',
  ...rest
}) => {
  const hasHeaderBadges = Array.isArray(headerBadges) && headerBadges.length > 0;
  const hasHeaderIcons = Array.isArray(headerIcons) && headerIcons.length > 0;
  const hasDropdown = Boolean(dropdown && Array.isArray(dropdown.items) && dropdown.items.length > 0);
  const hasHeaderActions = hasHeaderBadges || hasHeaderIcons || hasDropdown;

  const isStackedFooter = footerLayout === 'stacked';

  const hasFooter =
    (Array.isArray(footerMetaItems) && footerMetaItems.length > 0) ||
    (isStackedFooter && Array.isArray(footerMetaSecondaryItems) && footerMetaSecondaryItems.length > 0) ||
    (Array.isArray(footerRightItems) && footerRightItems.length > 0) ||
    Boolean(footerRight);

  const isClickable = typeof onClick === 'function';
  const handleRootKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(e);
      }
    },
    [onClick],
  );
  const handleSelectToggle = useCallback(
    (e) => {
      e.stopPropagation();
      onSelect?.(e);
    },
    [onSelect],
  );

  const borderClass = hasError
    ? 'border border-red-500/40 ring-1 ring-red-500/20'
    : selected
      ? 'border border-cyan-500/35 ring-1 ring-violet-500/25'
      : 'border border-edge dark:border-gray-700';

  const headerIconsVisibilityClass = showHeaderIconsOnHover
    ? 'hidden group-hover:inline-flex'
    : 'inline-flex';

  const menuIcon =
    renderLucideIcon(null, dropdown?.props?.triggerIconName, { size: 14 }) ??
    renderLucideIcon(DEFAULT_MENU_ICON, null, { size: 14 });

  const footerShellClass = isStackedFooter
    ? 'px-4 py-2.5'
    : `rounded-b-lg ${FOOTER_BAR_SURFACE_CLASS} px-4 py-2.5`;

  return (
    <div
      className={joinClassNames(
        'group relative rounded-lg transition-shadow hover:shadow-md',
        borderClass,
        isClickable ? 'cursor-pointer' : '',
        className,
      )}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? handleRootKeyDown : undefined}
      {...rest}
    >
      <EntityListCardSurface stacked={isStackedFooter}>
        {/* ── Zone 1: Header ── */}
        <div className="flex items-start gap-3 p-4 pb-2">
        {/* Selectable checkbox slot */}
        {selectable ? (
          <div className="shrink-0 pt-0.5">
            <button
              type="button"
              aria-label={selected ? 'Deselect' : 'Select'}
              onClick={handleSelectToggle}
              className="text-content-faint transition-colors hover:text-content"
            >
              {selected
                ? <CheckSquare size={16} className="text-brand-400" />
                : <Square size={16} />}
            </button>
          </div>
        ) : null}

        {/* Icon highlight + optional label below */}
        <div className="flex shrink-0 flex-col items-center gap-0.5">
          <IconHighlight icon={DEFAULT_ICON} size="small" {...iconHighlightProps} />
          {iconLabel ? (
            <span className="w-full truncate text-center text-[9px] font-semibold uppercase leading-tight text-content-faint">
              {iconLabel}
            </span>
          ) : null}
        </div>

        {/* Title row + body + actions */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            {/* Left: code + title + titleBadge */}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                {code ? (
                  <span className="inline-flex rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[10px] font-medium text-content-muted">
                    {code}
                  </span>
                ) : null}
                <p className="truncate text-sm font-semibold text-content">{title}</p>
                {titleBadge ? <TitleBadge {...titleBadge} /> : null}
              </div>

              {/* Free-form children */}
              {children ? <div className="mt-1 text-xs text-content-muted">{children}</div> : null}

              {/* bodyText with expand/collapse */}
              {bodyText ? (
                <EntityListCardBodyText
                  key={bodyText}
                  bodyText={bodyText}
                  bodyTextMaxLength={bodyTextMaxLength}
                  bodyIcon={BodyIcon}
                  moreLabel={moreLabel}
                  lessLabel={lessLabel}
                />
              ) : null}
            </div>

            {/* Right: header actions (badges/buttons + icon buttons + dropdown) */}
            {hasHeaderActions ? (
              <div
                className="ml-2 flex shrink-0 items-center gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                {hasHeaderBadges ? (
                  <div className="inline-flex items-center gap-1.5">
                    {headerBadges.map((item, idx) => (
                      <HeaderAction key={getMetaItemKey(item, idx, 'ha')} item={item} idx={idx} />
                    ))}
                  </div>
                ) : null}

                {hasHeaderIcons ? (
                  <div
                    className={[headerIconsVisibilityClass, 'items-center gap-1.5']
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {headerIcons.map((item, idx) => {
                      const iconElement = renderLucideIcon(item.icon, item.iconName, { size: 14 });
                      if (!iconElement) return null;
                      return (
                        <button
                          key={getMetaItemKey(item, idx, 'hi')}
                          type="button"
                          className={[
                            'rounded p-1 text-content-muted transition-colors hover:text-content',
                            item.className,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          aria-label={item.ariaLabel || item.title || `Action ${idx + 1}`}
                          title={item.title}
                          onClick={(e) => {
                            e.stopPropagation();
                            item.onClick?.();
                          }}
                        >
                          {iconElement}
                        </button>
                      );
                    })}
                  </div>
                ) : null}

                {hasDropdown ? (
                  <Dropdown
                    {...dropdown.props}
                    align={dropdown?.props?.align ?? 'right'}
                    trigger={
                      <button
                        type="button"
                        aria-label={dropdown?.props?.triggerAriaLabel || 'More actions'}
                        className="rounded p-1 text-content-muted transition-colors hover:text-content"
                      >
                        {menuIcon}
                      </button>
                    }
                  >
                    {dropdown.items.map((item, idx) =>
                      item.separator ? (
                        <Dropdown.Separator key={`ds-${idx}`} />
                      ) : (
                        <Dropdown.Item
                          key={getMetaItemKey(item, idx, 'di')}
                          icon={item.icon || resolveLucideIcon(item.iconName)}
                          variant={item.variant}
                          onClick={item.onClick}
                        >
                          {item.label}
                        </Dropdown.Item>
                      ),
                    )}
                  </Dropdown>
                ) : null}
              </div>
            ) : null}
          </div>

          {progress ? (
            <ProgressBar
              {...progress}
              className={['mt-3', progress.className].filter(Boolean).join(' ')}
            />
          ) : null}
        </div>
      </div>

      {/* ── Zone 2: Footer ── */}
      {hasFooter ? (
        <div className={footerShellClass}>
          {isStackedFooter ? (
            <div className="flex min-w-0 items-start justify-between gap-3">
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                {footerMetaItems.length > 0 ? (
                  <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
                    {footerMetaItems.map((item, idx) => (
                      <FooterMetaItem key={getMetaItemKey(item, idx, 'fm')} item={item} />
                    ))}
                  </div>
                ) : null}
                {footerMetaSecondaryItems.length > 0 ? (
                  <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
                    {footerMetaSecondaryItems.map((item, idx) => (
                      <FooterMetaItem key={getMetaItemKey(item, idx, 'fms')} item={item} />
                    ))}
                  </div>
                ) : null}
              </div>

              <FooterRightSlot
                footerRight={footerRight}
                footerRightItems={footerRightItems}
                stacked
              />
            </div>
          ) : (
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:gap-3">
                {footerMetaItems.map((item, idx) => (
                  <FooterMetaItem key={getMetaItemKey(item, idx, 'fm')} item={item} />
                ))}
              </div>

              <FooterRightSlot footerRight={footerRight} footerRightItems={footerRightItems} />
            </div>
          )}
        </div>
      ) : null}
      </EntityListCardSurface>
    </div>
  );
};

export default memo(EntityListCard);
